import moment from 'moment';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataView } from 'primereact/dataview';
import { Message } from 'primereact/message';
import { useState } from 'react';
import Data from '../../../../components/Display/Data';
import DataTag from '../../../../components/Display/DataTag';
import MultiLineData from '../../../../components/Display/MultiLineData';
import MultipleDataTag from '../../../../components/Display/MultipleDataTag';
import Section from '../../../../components/Display/Section';
import TranslationWarning from '../../../../components/Language/TranslationWarning';
import { getConstant } from '../../../../utils/constants';
import DocumentItem from '../../common/document/DocumentItem';
import ImageItem from '../../common/image/ImageItem';
import DistributionItem from '../distribution/DistributionItem';
import NewsListItem from '../news/NewsListItem';
import EventListItem from './EventListItem';

function ProgramDetail({
  program,
  handleUpdateProgram,
  handleDuplicateEvent,
  handleDeleteEvent,
  handleCreateEvent,
  handleUpdateEvent,
  handleUpdateDistribution,
  handleUpdateDistributionList,
  handleDeleteDistribution,
  handleClickResource,
  handleDelete,
  handleCreateFile,
  handleUpdateFile,
  handleDeleteFile,
  handleCreateNews,
  handleUpdateNews,
  handleDeleteNews
}) {
  const [imageisValid, setImageisValid] = useState(true);

  const updateProgramActions = [
    {
      onClick: handleUpdateProgram,
      label: 'Modifier les informations',
      icon: 'pi-pencil'
    }
  ];

  const newEventActions = [
    {
      onClick: handleCreateEvent,
      label: 'Ajouter un évènement',
      icon: 'pi-plus'
    }
  ];

  const newNewsActions = [
    {
      onClick: handleCreateNews,
      label: 'Ajouter un article',
      icon: 'pi-plus'
    }
  ];

  const editionDistributionActions = [
    {
      onClick: handleUpdateDistributionList,
      label: 'Modifier la distribution',
      icon: 'pi-pencil'
    }
  ];

  const newFileActions = [
    {
      onClick: handleCreateFile,
      label: 'Ajouter un fichier',
      icon: 'pi-plus'
    }
  ];

  const confirmDeletion = (program) => {
    confirmDialog({
      message: 'Êtes-vous sûr de vouloir supprimer ce Programme ?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: () => handleDelete(program)
    });
  };

  const confirmDocumentDeletion = (document) => {
    confirmDialog({
      message: 'Êtes-vous sûr de vouloir supprimer ce fichier ?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: () => handleDeleteFile(document)
    });
  };

  const confirmNewsDeletion = (news) => {
    confirmDialog({
      message: 'Êtes-vous sûr de vouloir supprimer cet article ?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Annuler',
      accept: () => handleDeleteNews(news)
    });
  };

  // Image is valid if width == height
  const computeImageShape = (img) => {
    setImageisValid(
      img.nativeEvent.srcElement.naturalHeight == img.nativeEvent.srcElement.naturalWidth
    );
  };

  const eventItemTemplate = (event) => {
    return (
      <EventListItem
        event={event}
        duplicateEvent={handleDuplicateEvent}
        deleteEvent={handleDeleteEvent}
        updateEvent={handleUpdateEvent}
      />
    );
  };

  const newsItemTemplate = (news) => {
    return (
      <NewsListItem news={news} deleteNews={confirmNewsDeletion} updateNews={handleUpdateNews} />
    );
  };

  return (
    <div className={`h-full w-full flex-col flex gap-5`}>
      <Section label="Informations Générales" actions={updateProgramActions}>
        <TranslationWarning />
        <div className="flex flex-wrap gap-5">
          <Data key="title" label={'Titre'} value={program.title} large translatable />
          <Data key="author" label={'Auteur'} value={program.author} large />
          <DataTag
            key="type"
            label="Type"
            tag={getConstant('banquetceleste.programTypes', program.type)}
            rounded
            large
          />
          <DataTag
            key="status"
            label="État"
            tag={getConstant('banquetceleste.common.statusTypes', program.status)}
            rounded
            large
          />
          <Data key="deezerId" label={'ID Deezer'} value={program.deezer_id} large />
          <Data key="video_url" label={'URL Youtube'} value={program.video_url} large />
        </div>
        <div className="flex flex-wrap gap-5">
          <Data
            key="date"
            label={'Date de publication'}
            value={moment(program.date).format('DD/MM/YYYY')}
            large
          />
          <MultipleDataTag
            key="properties"
            label={'Propriétés'}
            rounded
            tags={program.properties.map((property) => ({
              label: property.label
            }))}
          />
        </div>
        <div className="flex flex-wrap gap-5">
          <MultipleDataTag
            key="resources"
            label={'Ressources'}
            tags={program.resources.map((resource) => ({
              label: resource.title,
              icon: getConstant('banquetceleste.resourceTypes', resource.type).icon,
              onClick: () => handleClickResource(resource)
            }))}
            rounded
          />
        </div>
        <div className="flex flex-wrap gap-5">
          <MultiLineData
            key="description"
            label={'Description'}
            value={program.description}
            large
            asHtml
            translatable
          />
          <MultiLineData
            key="credits"
            label={'Crédits'}
            value={program.credits}
            large
            asHtml
            translatable
          />
        </div>
      </Section>
      <Section label="Image principale">
        <ImageItem image={program.image} imageLoad={computeImageShape} className="w-96" />
        {!imageisValid && (
          <div>
            <Message
              severity="error"
              text={`Cette image n'est pas de forme carrée, l'affichage sur le site vitrine ne sera pas optimal.`}
            />
          </div>
        )}
      </Section>
      <Section label="Fichiers téléchargeables" actions={newFileActions}>
        {program.files?.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {program.files.map((file) => (
              <DocumentItem
                key={file.id}
                document={file}
                onUpdate={() => handleUpdateFile(file)}
                onDelete={() => confirmDocumentDeletion(file)}
              />
            ))}
          </div>
        ) : (
          <p className="italic text-slate-400 font-light">Non renseignés</p>
        )}
      </Section>
      <Section label="Distribution" actions={editionDistributionActions}>
        <TranslationWarning />
        {program.distributions?.length > 0 ? (
          <div className="flex flex-col gap-3">
            {program.distributions.map((distrib, id) => (
              <DistributionItem
                key={id}
                distribution={distrib}
                handleUpdateDistribution={() => handleUpdateDistribution(distrib)}
                handleDeleteDistribution={() => handleDeleteDistribution(distrib)}
              />
            ))}
          </div>
        ) : (
          <p className="italic text-slate-400 font-light">Non renseignée</p>
        )}
      </Section>
      <Section label="Évènements" actions={newEventActions}>
        <DataView
          value={program.events}
          itemTemplate={eventItemTemplate}
          emptyMessage="Aucun évènement à afficher"
        />
      </Section>

      <Section label="Articles de presse" actions={newNewsActions}>
        <TranslationWarning />
        <DataView
          value={program.news}
          itemTemplate={newsItemTemplate}
          emptyMessage="Aucun article à afficher"
        />
      </Section>

      <Section label="Autres actions">
        <div className="flex flex-row">
          <Button
            key={'delete'}
            label={'Supprimer le programme'}
            className="!w-54"
            outlined
            onClick={() => confirmDeletion(program)}
            severity="danger"
          />
        </div>
      </Section>
    </div>
  );
}

export default ProgramDetail;
